<template>
  <div>
    <h4 class="table-page-header">
      Payroll Management
    </h4>

    <!-- Payroll Period Selection and Summary Section -->
    <div class="row search-wrapper">
      <div class="col-sm-4 mb-4">
        <AIVSelect
          v-model="selectedPeriodId"
          :options="formattedPeriodOptions"
          placeholder="Select Payroll Period"
          @change="onPeriodChange"
          :searchable="false"
          :clearable="false"
          :usePortal="false"
        />
      </div>
      <div class="col-sm-2 mb-4">
        <el-button
          type="success"
          @click="exportPayroll"
          :loading="loadingExport"
          :disabled="!selectedPeriod"
        >
          Export CSV
        </el-button>
      </div>
      <div class="col-sm-2 mb-4">
        <el-button
          type="primary"
          @click="showExportDialog = true"
        >
          Choose Dates for Export
        </el-button>
      </div>
    </div>

    <!-- Summary Display -->
    <div v-if="summary" class="row">
      <div class="col-md-3 col-sm-6">
        <div class="summary-card">
          <div class="card-header">
            <i class="fa fa-users summary-icon"></i>
            <span class="card-title">Employees</span>
          </div>
          <div class="card-value">{{ summary.totalEmployees }}</div>
        </div>
      </div>
      <div class="col-md-3 col-sm-6">
        <div class="summary-card">
          <div class="card-header">
            <i class="fa fa-clock summary-icon"></i>
            <span class="card-title">Regular Hours</span>
          </div>
          <div class="card-value">{{ summary.totalRegularHours }}</div>
        </div>
      </div>
      <div class="col-md-3 col-sm-6">
        <div class="summary-card">
          <div class="card-header">
            <i class="fa fa-clock summary-icon"></i>
            <span class="card-title">Overtime Hours</span>
          </div>
          <div class="card-value">{{ summary.totalOvertimeHours }}</div>
        </div>
      </div>
      <div class="col-md-3 col-sm-6">
        <div class="summary-card">
          <div class="card-header">
            <i class="fa fa-car summary-icon"></i>
            <span class="card-title">Mileage</span>
          </div>
          <div class="card-value">
            {{ summary.totalMiles != null ? summary.totalMiles : '—' }}
            <span v-if="summary.totalMileageAmount != null" class="mileage-amount">
              (${{ summary.totalMileageAmount.toFixed(2) }})
            </span>
          </div>
          <div
            v-if="summary.totalPendingMiles > 0"
            class="pending-miles-chip"
            title="Recorded miles not yet approved — approve them with their shifts to include them"
          >
            <i class="fa fa-triangle-exclamation"></i>
            {{ summary.totalPendingMiles }} mi awaiting approval
          </div>
        </div>
      </div>
    </div>

    <!-- Clocked Out Shifts Awaiting Approval -->
    <div class="mt-4">
      <h4 class="table-page-header">
        Shifts Awaiting Approval
      </h4>
      <AIVTable
        v-if="clockedOutShifts && clockedOutShifts.length > 0"
        :columns="clockedOutColumns"
        :data="clockedOutShifts"
        :pagination="clockedOutPagination"
        :item-name="'shifts'"
        @pageChange="handleClockedOutPageChange"
        @pageSizeChange="handleClockedOutPageSizeChange"
        @sort-change="handleClockedOutSortChange"
      />
      <div v-else-if="!loadingClockedOutShifts" class="text-center py-4">
        <p>No shifts awaiting approval.</p>
      </div>
    </div>

    <!-- Employee Details Table -->
    <div v-if="summary" class="mt-4">
      <h4 class="table-page-header">Employee Details</h4>
      <AIVTable
        v-if="employees && employees.length > 0"
        :columns="employeeColumns"
        :data="employees"
        :pagination="employeePagination"
        :item-name="'employees'"
        @pageChange="handleEmployeePageChange"
        @pageSizeChange="handleEmployeePageSizeChange"
        @sort-change="handleEmployeeSortChange"
      >
        <template #cell-checkbox="{ row }">
          <el-checkbox
            :value="!excludedEmployees[row.employeeId]"
            @change="toggleEmployee(row.employeeId, $event)"
          />
        </template>
      </AIVTable>
      <div v-else-if="!loadingSummary" class="text-center py-4">
        <p>No employee data found for the selected date range.</p>
      </div>
    </div>

    <!-- Payroll Batch History -->
    <h4 class="table-page-header mt-5">
      Payroll Batch History
    </h4>

    <AIVTable
      :columns="batchColumns"
      :data="batches"
      :pagination="batchPagination"
      :item-name="'batches'"
      @pageChange="fetchBatchHistory"
      @pageSizeChange="handleBatchPageSizeChange"
      @sort-change="handleBatchSortChange"
    >
      <template #cell-actions="{ row }">
        <button
          class="btn btn-sm btn-success"
          @click="downloadBatchCSV(row)"
          title="Download CSV"
        >
          <i class="fa-solid fa-download"></i>
        </button>
      </template>
    </AIVTable>

    <!-- Export Dialog -->
    <AIVDialog
      v-model="showExportDialog"
      title="Export Payroll"
      width="40%"
      height="auto"
      class="payroll-export-modal"
    >
      <el-form label-position="top" label-width="120px">
        <div class="row">
          <div class="col-md-12">
            <label>Date Range</label>
            <el-date-picker
              v-model="exportDateRange"
              type="daterange"
              range-separator="-"
              start-placeholder="Start date"
              end-placeholder="End date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              style="width: 100%"
            />
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-12">
            <el-checkbox v-model="saveBatch">Save as batch for history</el-checkbox>
          </div>
        </div>
      </el-form>
      <template slot="footer">
        <div class="text-center">
          <el-button round @click="showExportDialog = false">Cancel</el-button>
          <el-button
            type="primary"
            round
            @click="confirmExport"
            :loading="loadingExport"
          >
            Export
          </el-button>
        </div>
      </template>
    </AIVDialog>

    <!-- Batch Details Dialog -->
    <AIVDialog
      v-model="showBatchDetails"
      title="Batch Details"
      width="90%"
      height="85vh"
      class="payroll-batch-modal"
    >
      <div v-if="selectedBatch">
        <div class="row">
          <div class="col-md-6">
            <p><strong>Batch Number:</strong> {{ selectedBatch.batch_number }}</p>
            <p><strong>Date Range:</strong> {{ selectedBatch.start_date }} to {{ selectedBatch.end_date }}</p>
            <p><strong>Status:</strong>
              <span :class="getStatusClass(selectedBatch.status)">
                {{ formatStatus(selectedBatch.status) }}
              </span>
            </p>
          </div>
          <div class="col-md-6">
            <p><strong>Employee Count:</strong> {{ selectedBatch.employee_count }}</p>
            <p><strong>Total Hours:</strong> {{ selectedBatch.total_hours }}</p>
            <p><strong>Created:</strong> {{ formatDate(selectedBatch.createdAt) }}</p>
          </div>
        </div>
        <div v-if="selectedBatch.notes" class="mt-3">
          <p><strong>Notes:</strong></p>
          <p>{{ selectedBatch.notes }}</p>
        </div>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button round @click="showBatchDetails = false">Close</el-button>
        </div>
      </template>
    </AIVDialog>

    <!-- Status Change Dialog -->
    <AIVDialog
      v-model="showStatusDialog"
      title="Change Batch Status"
      width="90%"
      height="85vh"
      class="payroll-status-modal"
    >
      <el-form label-position="top" label-width="120px">
        <div class="row">
          <div class="col-md-12">
            <label>Status</label>
            <AIVSelect v-model="newStatus" :options="statusOptions" :searchable="false" :clearable="false" placeholder="Select status" />
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-12">
            <label>Notes (optional)</label>
            <el-input
              v-model="statusNotes"
              type="textarea"
              :rows="3"
              placeholder="Add notes about this status change..."
            />
          </div>
        </div>
      </el-form>
      <template slot="footer">
        <div class="text-center">
          <el-button round @click="showStatusDialog = false">Cancel</el-button>
          <el-button
            type="primary"
            round
            @click="updateBatchStatus"
            :loading="loadingStatusUpdate"
          >
            Update
          </el-button>
        </div>
      </template>
    </AIVDialog>

    <!-- Portal target for AIVSelect dropdowns -->
    <div id="aiv-portals"></div>
  </div>
</template>

<script>
import AIVTable from '@/components/AIVTable.vue'
import AIVDialog from '@/components/AIVDialog.vue'
import AIVSelect from '@/components/AIVSelect.vue'
import { payrollService } from '@/serviceHandlers/payrollService'
import { payrollPeriodsService } from '@/serviceHandlers/payrollPeriodsService'

export default {
  name: 'Payroll',
  components: {
    AIVTable,
    AIVDialog,
    AIVSelect
  },
  data() {
    return {
      selectedPeriodId: null,
      selectedPeriod: null,
      payrollPeriods: [],
      exportDateRange: this.getDefaultDateRange(),
      saveBatch: true,
      summary: null,
      employees: [],
      excludedEmployees: {},
      batches: [],
      clockedOutShifts: [],
      selectedBatch: null,
      selectedBatchForStatus: null,
      showExportDialog: false,
      showBatchDetails: false,
      showStatusDialog: false,
      newStatus: '',
      statusNotes: '',
      statusOptions: [
        { id: "exported", name: "Exported" },
        { id: "processed", name: "Processed" },
        { id: "cancelled", name: "Cancelled" },
      ],
      loadingSummary: false,
      loadingExport: false,
      loadingHistory: false,
      loadingStatusUpdate: false,
      loadingPeriods: false,
      loadingClockedOutShifts: false,
      batchSortProp: '',
      batchSortOrder: '',
      clockedOutSortProp: '',
      clockedOutSortOrder: '',
      employeeSortProp: '',
      employeeSortOrder: '',
      employeePagination: {
        currentPage: 1,
        perPage: 20,
        total: 0,
        totalPages: 1
      },
      batchPagination: {
        currentPage: 1,
        perPage: 20,
        total: 0,
        totalPages: 1
      },
      clockedOutPagination: {
        currentPage: 1,
        perPage: 20,
        total: 0,
        totalPages: 1
      },
      employeeColumns: [
        { key: 'checkbox', label: '', sortable: false, width: 50 },
        { key: 'employeeName', label: 'Employee Name', sortable: true },
        { key: 'fileNumber', label: 'File #', sortable: true },
        { key: 'shiftCount', label: 'Shifts', sortable: true },
        { key: 'regularHours', label: 'Regular Hours', sortable: true },
        { key: 'overtimeHours', label: 'Overtime Hours', sortable: true },
        { key: 'totalHours', label: 'Total Hours', sortable: true },
        { key: 'totalMiles', label: 'Mileage', sortable: true, format: (value) => value != null ? value.toFixed(1) : '0.0' },
        { key: 'mileageAmount', label: 'Mileage ($)', sortable: true, format: (value) => value != null ? `$${value.toFixed(2)}` : '$0.00' },
        { key: 'pendingMiles', label: 'Pending Mi', sortable: true, format: (value) => value > 0 ? value.toFixed(1) : '—' },
        {
          key: 'allowanceUsed', label: 'Allowance', sortable: false,
          format: (value) => {
            const cap = this.summary && this.summary.mileageCap;
            if (value == null || cap == null) return '—';
            const pct = cap > 0 ? Math.round((value / cap) * 100) : 0;
            return `$${value.toFixed(2)} of $${Number(cap).toFixed(2)} (${pct}%)`;
          },
        }
      ],
      batchColumns: [
        { key: 'batch_number', label: 'Batch Number', sortable: true },
        { key: 'start_date', label: 'Start Date', sortable: true },
        { key: 'end_date', label: 'End Date', sortable: true },
        { key: 'employee_count', label: 'Employees', sortable: true },
        { key: 'total_hours', label: 'Total Hours', sortable: true },
        { key: 'createdAt', label: 'Created', sortable: true, format: (value) => this.formatDate(value) },
        { key: 'actions', label: 'Actions', sortable: false, width: 200 }
      ],
      clockedOutColumns: [
        { key: 'caregiverName', label: 'Employee', sortable: true },
        { key: 'status', label: 'Status', sortable: false, format: (value) => value === 'Error' ? '⚠ Error' : 'Clocked Out' },
        { key: 'clockIn', label: 'Clock In', sortable: true, format: (value) => this.formatDateTime(value) },
        { key: 'clockOut', label: 'Clock Out', sortable: true, format: (value) => this.formatDateTime(value) },
        { key: 'hours', label: 'Hours', sortable: true, format: (value) => value ? value.toFixed(2) : '0.00' },
        { key: 'shiftType', label: 'Type', sortable: true },
        { key: 'payer', label: 'Payer', sortable: true }
      ]
    }
  },
  computed: {
    formattedPeriodOptions() {
      return this.payrollPeriods.map(period => ({
        id: period.id || `${period.year}-${period.month}-${period.period_type}`,
        name: `${this.formatDate(period.start_date)} - ${this.formatDate(period.end_date)}`
      }))
    }
  },
  mounted() {
    this.fetchPayrollPeriods()
    this.fetchBatchHistory()
  },
  methods: {
    async fetchPayrollPeriods() {
      this.loadingPeriods = true
      try {
        const response = await payrollPeriodsService.getUpcomingPeriods({ limit: 12 })
        this.payrollPeriods = response.data
        if (this.payrollPeriods.length > 0) {
          const currentPeriod = this.payrollPeriods.find(p => p.status === 'current') || this.payrollPeriods[0]
          this.selectedPeriodId = currentPeriod.id || `${currentPeriod.year}-${currentPeriod.month}-${currentPeriod.period_type}`
          this.selectedPeriod = currentPeriod
          this.getPayrollSummary()
          this.fetchClockedOutShifts()
        }
      } catch (error) {
        this.$notifyError('Failed to fetch payroll periods: ' + error.message)
      } finally {
        this.loadingPeriods = false
      }
    },
    onPeriodChange(periodId) {
      this.selectedPeriod = this.payrollPeriods.find(p =>
        (p.id || `${p.year}-${p.month}-${p.period_type}`) === periodId
      )
      if (this.selectedPeriod) {
        this.excludedEmployees = {}
        this.getPayrollSummary()
        this.fetchClockedOutShifts()
      }
    },
    async getPayrollSummary(page = 1) {
      if (!this.selectedPeriod) {
        this.$notifyError('Please select a payroll period')
        return
      }

      this.loadingSummary = true
      try {
        const response = await payrollService.getPayrollSummary({
          startDate: this.selectedPeriod.start_date,
          endDate: this.selectedPeriod.end_date,
          page: page,
          limit: this.employeePagination.perPage,
          sortField: this.employeeSortProp,
          sortOrder: this.employeeSortOrder,
        })
        this.summary = response.summary
        // allowanceUsed mirrors mileageAmount under its own key so the
        // Allowance column can format it independently.
        this.employees = (response.employees || []).map((e) => ({
          ...e,
          allowanceUsed: e.mileageAmount,
        }))

        // Update pagination from backend response
        if (response.pagination) {
          this.employeePagination = {
            currentPage: response.pagination.page,
            perPage: response.pagination.limit,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          }
        }

      } catch (error) {
        this.$notifyError('Failed to get payroll summary: ' + error.message)
      } finally {
        this.loadingSummary = false
      }
    },
    async exportPayroll() {
      if (!this.selectedPeriod) {
        this.$notifyError('Please select a payroll period')
        return
      }

      this.loadingExport = true
      try {
        const params = {
          startDate: this.selectedPeriod.start_date,
          endDate: this.selectedPeriod.end_date,
          saveBatch: true
        }
        const excludedIds = Object.keys(this.excludedEmployees).filter(id => this.excludedEmployees[id])
        if (excludedIds.length > 0) {
          params.excludeEmployeeIds = excludedIds.join(',')
        }
        await payrollService.exportPayroll(params)
        this.$notifySuccess('Payroll exported successfully')
        this.fetchBatchHistory()
      } catch (error) {
        // Use $notifyError for "No approved shifts" message
        if (error.message && error.message.includes('No approved shifts')) {
          this.$notifyError(error.message)
        } else {
          this.$notifyError('Failed to export payroll: ' + error.message)
        }
      } finally {
        this.loadingExport = false
      }
    },
    async confirmExport() {
      if (!this.exportDateRange || this.exportDateRange.length !== 2) {
        this.$notifyError('Please select a date range')
        return
      }

      this.loadingExport = true
      try {
        const params = {
          startDate: this.exportDateRange[0],
          endDate: this.exportDateRange[1],
          saveBatch: this.saveBatch
        }
        const excludedIds = Object.keys(this.excludedEmployees).filter(id => this.excludedEmployees[id])
        if (excludedIds.length > 0) {
          params.excludeEmployeeIds = excludedIds.join(',')
        }
        await payrollService.exportPayroll(params)
        this.$notifySuccess('Payroll exported successfully')
        this.showExportDialog = false
        this.exportDateRange = []
        this.fetchBatchHistory()
      } catch (error) {
        // Use $notifyError for "No approved shifts" message
        if (error.message && error.message.includes('No approved shifts')) {
          this.$notifyError(error.message)
        } else {
          this.$notifyError('Failed to export payroll: ' + error.message)
        }
      } finally {
        this.loadingExport = false
      }
    },
    async fetchBatchHistory(page = 1) {
      this.loadingHistory = true
      try {
        const response = await payrollService.getPayrollBatchHistory({
          page: page,
          limit: this.batchPagination.perPage,
          sortBy: this.batchSortProp,
          order: this.batchSortOrder,
        })
        this.batches = response.data
        this.batchPagination = {
          currentPage: response.pagination.page,
          perPage: response.pagination.limit,
          total: response.pagination.total,
          totalPages: Math.ceil(response.pagination.total / response.pagination.limit)
        }
      } catch (error) {
        this.$notifyError('Failed to fetch batch history: ' + error.message)
      } finally {
        this.loadingHistory = false
      }
    },
    async viewBatchDetails(batch) {
      try {
        const response = await payrollService.getPayrollBatchDetails(batch.id)
        this.selectedBatch = response.data
        this.showBatchDetails = true
      } catch (error) {
        this.$notifyError('Failed to fetch batch details: ' + error.message)
      }
    },
    async downloadBatchCSV(batch) {
      try {
        await payrollService.downloadPayrollBatchCSV(batch.id)
        this.$notifySuccess('CSV download started')
      } catch (error) {
        this.$notifyError('Failed to download CSV: ' + error.message)
      }
    },
    showStatusDropdown(batch) {
      this.selectedBatchForStatus = batch
      this.newStatus = batch.status
      this.statusNotes = ''
      this.showStatusDialog = true
    },
    async updateBatchStatus() {
      if (!this.newStatus) {
        this.$notifyError('Please select a status')
        return
      }

      this.loadingStatusUpdate = true
      try {
        await payrollService.updatePayrollBatchStatus(this.selectedBatchForStatus.id, {
          status: this.newStatus,
          notes: this.statusNotes
        })
        this.$notifySuccess('Batch status updated successfully')
        this.showStatusDialog = false
        this.fetchBatchHistory()
      } catch (error) {
        this.$notifyError('Failed to update batch status: ' + error.message)
      } finally {
        this.loadingStatusUpdate = false
      }
    },
    toggleEmployee(employeeId, checked) {
      if (checked) {
        this.$delete(this.excludedEmployees, employeeId)
      } else {
        this.$set(this.excludedEmployees, employeeId, true)
      }
    },
    handleEmployeePageChange(page) {
      this.getPayrollSummary(page)
    },
    handleEmployeePageSizeChange(newSize) {
      this.employeePagination.perPage = newSize
      // Reset to page 1 when changing page size
      this.getPayrollSummary(1)
    },
    async fetchClockedOutShifts(page = 1) {
      this.loadingClockedOutShifts = true
      try {
        const params = {
          page: page,
          limit: this.clockedOutPagination.perPage,
          sortField: this.clockedOutSortProp,
          sortOrder: this.clockedOutSortOrder,
        }
        if (this.selectedPeriod) {
          params.startDate = this.selectedPeriod.start_date
          params.endDate = this.selectedPeriod.end_date
        }
        const response = await payrollService.getClockedOutShifts(params)
        this.clockedOutShifts = response.data || []
        this.clockedOutPagination = {
          currentPage: response.pagination.page,
          perPage: response.pagination.limit,
          total: response.pagination.total,
          totalPages: Math.ceil(response.pagination.total / response.pagination.limit)
        }
      } catch (error) {
        this.$notifyError('Failed to fetch clocked-out shifts: ' + error.message)
      } finally {
        this.loadingClockedOutShifts = false
      }
    },
    handleClockedOutPageChange(page) {
      this.fetchClockedOutShifts(page)
    },
    handleClockedOutPageSizeChange(newSize) {
      this.clockedOutPagination.perPage = newSize
      // Reset to page 1 when changing page size
      this.fetchClockedOutShifts(1)
    },
    handleBatchSortChange({ prop, order }) {
      this.batchSortProp = prop
      this.batchSortOrder = order
      this.fetchBatchHistory(1)
    },
    handleClockedOutSortChange({ prop, order }) {
      this.clockedOutSortProp = prop
      this.clockedOutSortOrder = order
      this.fetchClockedOutShifts(1)
    },
    handleEmployeeSortChange({ prop, order }) {
      this.employeeSortProp = prop
      this.employeeSortOrder = order
      this.getPayrollSummary(1)
    },
    handleBatchPageSizeChange(newSize) {
      this.batchPagination.perPage = newSize
      // Reset to page 1 when changing page size
      this.fetchBatchHistory(1)
    },
    formatDate(date) {
      if (!date) return ''
      // Handle ISO datetime strings (e.g., "2024-01-15T10:30:00.000Z")
      if (date.includes('T')) {
        const d = new Date(date)
        return d.toLocaleDateString()
      }
      // Handle simple date strings (e.g., "2024-01-15")
      const parts = date.split('-')
      const utcDate = new Date(parts[0], parts[1] - 1, parts[2])
      return utcDate.toLocaleDateString()
    },
    formatDateTime(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleString()
    },
    formatShiftDate(date) {
      if (!date) return ''
      // Parse date as UTC to prevent timezone shifting
      const parts = date.split('-')
      const d = new Date(parts[0], parts[1] - 1, parts[2])
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },
    formatStatus(status) {
      return status.charAt(0).toUpperCase() + status.slice(1)
    },
    getStatusClass(status) {
      const classes = {
        'draft': 'badge badge-secondary',
        'exported': 'badge badge-primary',
        'processed': 'badge badge-success',
        'cancelled': 'badge badge-danger'
      }
      return classes[status] || 'badge badge-secondary'
    },
    getDefaultDateRange() {
      const today = new Date()
      const twoWeeksAgo = new Date(today)
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 13) // 13 days ago + today = 14 days total

      const formatDate = (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }

      return [formatDate(twoWeeksAgo), formatDate(today)]
    }
  }
}
</script>

<style scoped>
.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.badge-primary {
  background-color: #007bff;
  color: white;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

/* Clean summary card design */
.summary-card {
  background: #253741;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  transition: box-shadow 0.2s ease;
}

.mileage-amount {
  font-size: 0.65em;
  color: rgba(255, 255, 255, 0.75);
}

.pending-miles-chip {
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: #e6a23c;
  border: 1px solid rgba(230, 162, 60, 0.45);
  background: rgba(230, 162, 60, 0.12);
  border-radius: 10px;
  padding: 1px 8px;
}

.summary-card:hover {
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.4);
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.summary-icon {
  font-size: 20px;
  margin-right: 10px;
  color: #4caf50;
  width: 24px;
  text-align: center;
}

.card-title {
  font-size: 14px;
  color: #b3a6a6;
  font-weight: 500;
  margin: 0;
}

.card-value {
  font-size: 28px;
  font-weight: bold;
  color: #ffffff;
  line-height: 1;
  text-align: center;
}

/* Shifts display styling */
.shifts-container {
  max-height: 120px;
  overflow-y: auto;
  padding: 5px;
}

.shift-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 6px;
  margin-bottom: 2px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  font-size: 12px;
}

.shift-date {
  color: #e0e0e0;
  font-weight: 500;
  min-width: 40px;
}

.shift-hours {
  color: #4caf50;
  font-weight: bold;
  min-width: 30px;
}

.overtime-badge {
  background: #ff9800;
  color: white;
  padding: 1px 4px;
  border-radius: 2px;
  font-size: 10px;
  font-weight: bold;
}

.no-shifts {
  color: #999;
  font-style: italic;
  font-size: 12px;
  text-align: center;
  padding: 10px;
}

/* Export Dialog Styles */
.payroll-export-modal ::v-deep .el-date-editor {
  width: 100%;
}

.payroll-export-modal ::v-deep .el-date-editor .el-input__inner {
  background-color: #656565 !important;
  border: 1px solid #4a5568;
  color: #ffffff;
}

.payroll-export-modal ::v-deep .el-date-editor .el-input__inner:focus {
  border-color: #4caf50;
}

.payroll-export-modal ::v-deep .el-date-editor .el-input__icon {
  color: #a0aec0;
}

.payroll-export-modal ::v-deep .el-date-editor--daterange {
  width: 100% !important;
}

.payroll-export-modal ::v-deep .el-date-editor--daterange .el-input__suffix {
  right: 10px !important;
}

.payroll-export-modal ::v-deep .el-range-separator {
  color: #ffffff !important;
  padding: 0 5px !important;
}

.payroll-export-modal ::v-deep .el-range-input {
  background-color: #656565 !important;
  color: #ffffff !important;
}

.payroll-export-modal ::v-deep .el-range__close-icon {
  right: 10px !important;
}

.payroll-export-modal ::v-deep .el-date-editor--daterange .el-range__icon {
  color: #a0aec0;
}

.payroll-export-modal ::v-deep .el-checkbox__label {
  color: #ffffff;
}

.payroll-export-modal ::v-deep .el-checkbox__inner {
  background-color: #656565;
  border-color: #ffffff;
}

.payroll-export-modal ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: #4caf50;
  border-color: #4caf50;
}

.payroll-export-modal label {
  color: #e0e0e0;
  font-weight: 500;
  margin-bottom: 8px;
  display: block;
}

</style>

<style>
/* Fix the day headers to show single letters */
.el-date-table th {
  font-size: 0 !important;
  padding: 5px 0 !important;
  position: relative !important;
}

.el-date-table th::before {
  content: attr(title);
  display: block;
  font-size: 12px !important;
  text-transform: uppercase;
}

/* Override with single letters */
.el-date-range-picker__content:nth-child(1) .el-date-table th:nth-child(1)::before,
.el-date-range-picker__content:nth-child(2) .el-date-table th:nth-child(1)::before { content: "S" !important; font-size: 12px !important; }
.el-date-range-picker__content:nth-child(1) .el-date-table th:nth-child(2)::before,
.el-date-range-picker__content:nth-child(2) .el-date-table th:nth-child(2)::before { content: "M" !important; font-size: 12px !important; }
.el-date-range-picker__content:nth-child(1) .el-date-table th:nth-child(3)::before,
.el-date-range-picker__content:nth-child(2) .el-date-table th:nth-child(3)::before { content: "T" !important; font-size: 12px !important; }
.el-date-range-picker__content:nth-child(1) .el-date-table th:nth-child(4)::before,
.el-date-range-picker__content:nth-child(2) .el-date-table th:nth-child(4)::before { content: "W" !important; font-size: 12px !important; }
.el-date-range-picker__content:nth-child(1) .el-date-table th:nth-child(5)::before,
.el-date-range-picker__content:nth-child(2) .el-date-table th:nth-child(5)::before { content: "T" !important; font-size: 12px !important; }
.el-date-range-picker__content:nth-child(1) .el-date-table th:nth-child(6)::before,
.el-date-range-picker__content:nth-child(2) .el-date-table th:nth-child(6)::before { content: "F" !important; font-size: 12px !important; }
.el-date-range-picker__content:nth-child(1) .el-date-table th:nth-child(7)::before,
.el-date-range-picker__content:nth-child(2) .el-date-table th:nth-child(7)::before { content: "S" !important; font-size: 12px !important; }
</style>